import React, { useEffect, useState } from "react";
import { formatDateInDDMMYYYY } from "src/helpers/common";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { getAppointmentTimeSlotsRequest } from "src/redux/slices/appointmentsSlice";
import { getAppointmentSlotRequest } from "src/redux/slices/labTestSlice";
import moment from "moment";
import { AddressListResponse } from "src/types/location.types";
import { ClockAlert } from "lucide-react";
import Loader from "src/components/common/Loader";
import { ScheduleScreenNames } from "src/types/common";
import { SCHEDULE_SCREEN_NAMES } from "src/constants/common";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

interface TimeSlotsViewProps {
  isSelectedDateChanged: Date | null;
  onTimeSlotSelect: (time: string, slotId: string | null) => void;
  screenName: ScheduleScreenNames;
  address?: AddressListResponse;
}

const TimeSlotsView: React.FC<TimeSlotsViewProps> = ({
  isSelectedDateChanged,
  onTimeSlotSelect,
  screenName,
  address,
}) => {
  const dispatch = useAppDispatch();
  const [isTimeSlotLoading, setIsTimeSlotLoading] = useState(false);
  const appointmentTimeSlots = useAppSelector(
    (state) => state.appointments.appointmentTimeSlots
  );
  const labTestAppointmentTimeSlots = useAppSelector(
    (state) => state.labTest.getAppointmentSlot
  );
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const coachId = useAppSelector(
    (state) => state.appointments.coach?.health_coach_id
  );
  const [isTimeSlotClicked, setIsTimeSlotClicked] = useState(false);
  // Reset selected time slot when date changes
  useEffect(() => {
    setSelectedTimeSlot(null);
    onTimeSlotSelect("", null); // Notify parent that time slot is cleared
    setIsTimeSlotClicked(false);
  }, [isSelectedDateChanged]);

  useEffect(() => {
    setIsTimeSlotLoading(true);

    new Promise((resolve, reject) => {
      const formattedDate = formatDateInDDMMYYYY(isSelectedDateChanged);
      if (screenName === SCHEDULE_SCREEN_NAMES.BOOK_CONSULTATION) {
        dispatch(
          getAppointmentTimeSlotsRequest({
            resolve,
            reject,
            payload: {
              appointment_date: formattedDate,
              consulation_type: "Video",
              health_coach_id: coachId,
              type: "Coach",
            },
          })
        );
      } else if (screenName === SCHEDULE_SCREEN_NAMES.BOOK_LAB_TEST) {
        dispatch(
          getAppointmentSlotRequest({
            resolve,
            reject,
            payload: {
              Pincode: address?.pincode,
              Date: moment(isSelectedDateChanged).format("YYYY-MM-DD"),
              vendor_flag: "redcliff",
              latitude: address?.latitude,
              longitude: address?.longitude,
            },
          })
        );
      }
    })
      .then(() => {
        // Event: SLOT_SELECTION_SUCCESS
        analytics.trackEvent(EVENT_NAMES.AVAILABLE_SLOTS_LOAD_SUCCESS, {
          screen_name: `${screenName}_${SCREEN_NAMES.SLOT_SELECTION_SCREEN}`,
          ...(screenName === SCHEDULE_SCREEN_NAMES.BOOK_CONSULTATION && {
            coach_id: coachId,
          }),
          ...(screenName === SCHEDULE_SCREEN_NAMES.BOOK_LAB_TEST && {
            ...address,
          }),
        });
      })
      .catch((error) => {
        // Event: SLOT_SELECTION_ERROR
        analytics.trackEvent(EVENT_NAMES.AVAILABLE_SLOTS_LOAD_ERROR, {
          screen_name: `${screenName}_${SCREEN_NAMES.SLOT_SELECTION_SCREEN}`,
          error: error.message,
          ...(screenName === SCHEDULE_SCREEN_NAMES.BOOK_CONSULTATION && {
            coach_id: coachId,
          }),
          ...(screenName === SCHEDULE_SCREEN_NAMES.BOOK_LAB_TEST && {
            ...address,
          }),
        });
      })
      .finally(() => {
        setIsTimeSlotLoading(false);
      });
  }, [isSelectedDateChanged]);

  const onTimeSlotClick = (time: string, slotId: string | null) => {
    // Event: SLOT_SELECTION_CLICKED
    analytics.trackEvent(EVENT_NAMES.SLOT_SELECTION_CLICKED, {
      screen_name: `${screenName}_${SCREEN_NAMES.SLOT_SELECTION_SCREEN}`,
      selected_time: time,
      selected_slot_id: slotId,
      ...(screenName === SCHEDULE_SCREEN_NAMES.BOOK_CONSULTATION && {
        coach_id: coachId,
      }),
      ...(screenName === SCHEDULE_SCREEN_NAMES.BOOK_LAB_TEST && {
        ...address,
      }),
    });
    setIsTimeSlotClicked(true);
    setSelectedTimeSlot(time);
    onTimeSlotSelect(time, slotId);
  };

  const hasConsultationSlots =
    screenName === SCHEDULE_SCREEN_NAMES.BOOK_CONSULTATION &&
    appointmentTimeSlots?.data?.result?.time_slot?.some(
      (slot: { slots: string[] }) =>
        Array.isArray(slot?.slots) && slot.slots.length > 0
    );

  const hasLabTestSlots =
    screenName === SCHEDULE_SCREEN_NAMES.BOOK_LAB_TEST &&
    labTestAppointmentTimeSlots?.data?.some(
      (slotGroup: any) =>
        Array.isArray(slotGroup?.slots) && slotGroup.slots.length > 0
    );

  const hasSlots = hasConsultationSlots || hasLabTestSlots;

  return (
    <div
      className={`flex-1 ${
        screenName === SCHEDULE_SCREEN_NAMES.BOOK_LAB_TEST
          ? "h-[calc(100vh-20rem)]"
          : isTimeSlotClicked
          ? "h-[calc(100vh-20rem-72px)]"
          : "h-[calc(100vh-14rem)]"
      }`}
    >
      {isTimeSlotLoading ? (
        <div className="flex items-center justify-center h-full">
          <Loader />
        </div>
      ) : hasSlots ? (
        <div className="grid grid-cols-2 gap-3 pb-4">
          {screenName === SCHEDULE_SCREEN_NAMES.BOOK_CONSULTATION &&
            appointmentTimeSlots?.data?.result?.time_slot?.map(
              (slot: { slots: string[] }, groupIndex: number) =>
                slot.slots.map((time_slot: string, index: number) => (
                  <Tile
                    key={`${groupIndex}-${index}`}
                    tileContent={time_slot}
                    onClick={() => {
                      onTimeSlotClick(time_slot, null);
                    }}
                    isActive={selectedTimeSlot === time_slot}
                  />
                ))
            )}

          {screenName === SCHEDULE_SCREEN_NAMES.BOOK_LAB_TEST &&
            labTestAppointmentTimeSlots?.data?.map(
              (slotGroup: any, groupIndex: number) =>
                slotGroup.slots.map((slot: any, index: number) => (
                  <Tile
                    key={`${groupIndex}-${index}`}
                    tileContent={slot.time}
                    onClick={() => {
                      onTimeSlotClick(slot.time, slot.slot_id);
                    }}
                    isActive={selectedTimeSlot === slot.time}
                  />
                ))
            )}
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <NoTimeSlotsAvailable />
        </div>
      )}
    </div>
  );
};

export default TimeSlotsView;

const NoTimeSlotsAvailable = () => {
  return (
    <div className="flex flex-col items-center justify-center gap-3 h-full col-span-2">
      <div className="bg-gray-200 rounded-full p-3">
        <ClockAlert className="w-6 h-6 text-gray-500" />
      </div>
      <p className="text-sm text-gray-500">No time slots available</p>
    </div>
  );
};

const Tile = ({
  tileContent,
  onClick,
  isActive,
}: {
  tileContent: string;
  onClick: () => void;
  isActive: boolean;
}) => {
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-lg border-2 text-center transition-all ${
        isActive
          ? "border-[var(--app-primary)] bg-white"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex flex-col items-center">
        <div
          className={`font-medium text-sm ${
            isActive ? "text-[var(--app-primary)]" : "text-gray-800"
          }`}
        >
          {tileContent}
        </div>
      </div>
    </button>
  );
};
